import React, { useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import {
  SliderCard,
  TextInput,
  FormAutoComplete,
  buildNotification,
  GenerateNotification,
  isValidate,
} from 'smart-react';
import { Loader } from 'smart-react';
import { filterBy } from '@progress/kendo-data-query';
import { isFromValid } from 'smart-react';
import { Error } from '@progress/kendo-react-labels';
import { Field, Form } from '@progress/kendo-react-form';
import LookupContentSlider from './LookupContentSlider';
import { createLookup, updateLookup } from '../../Services/LookupDetailService';
import { listLookup } from '../../../Lookup/Services/LookupService';
import useQueryParams from '../../../../Core/CustomHooks/useQueryParams';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../../constants/eventDataTypes';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../../constants/notificationMessages';
/**
 * AddLookupDetails component
 * @param {Object} props - Component props
 */
const AddLookupDetails = ({
  show,
  selectedParent,
  selectedLookup,
  setShowSlider,
  editMode,
  onSave,
  isRefresh,
  setIsRefresh,
  editType,
}) => {
  const requiredFields = ['Type', 'DisplayName', 'Value'];
  const [loader, setLoader] = useState(false);
  const [validateForm, setValidateForm] = React.useState(false);
  const [lookupTypes, setLookupTypes] = useState([]);
  const [originalLookupTypes, setOriginalLookupTypes] = React.useState([]);
  const { removeParam } = useQueryParams();
  const [formData, setFormData] = React.useState({
    Type: editMode ? selectedLookup?.Type : selectedParent?.dataSet?.Type,
    LookupId: selectedLookup?.LookupId ?? '',
    DisplayName: selectedLookup?.DisplayName ?? '',
    Value: selectedLookup?.Value ?? '',
    Active: selectedLookup?.Active ?? true,
    NumberOfChilds: selectedLookup?.NumberOfChilds ?? 0,
    DisplayOrder: selectedLookup?.DisplayOrder ?? 0,
  });

  /**
   * Filter the Data
   * @value string
   */
  const filterData = (value) => {
    const data = originalLookupTypes;
    const filter = {
      field: 'Type',
      value: value,
      operator: 'startswith',
      ignoreCase: true,
    };
    return value ? filterBy(data, filter) : data;
  };

  /**
   * handle Input Change
   * @event object
   */
  const onChange = (event) => {
    const value = event.value;
    setLookupTypes(filterData(value));
    setFormData((prevState) => ({
      ...prevState,
      Type: event.value,
    }));
  };

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * Save the selected flow step
   */
  const handleSave = async () => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }
    setLoader(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
    };
    let response;
    let method;

    if (editMode) {
      method = updateLookup;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createLookup;
      notificationMetaData = CREATE_MESSAGE;
      setFormData({
        Type: editMode ? selectedLookup?.Type : selectedParent?.dataSet?.Type,
        LookupId: selectedLookup?.LookupId ?? '',
        DisplayName: selectedLookup?.DisplayName ?? '',
        Value: selectedLookup?.Value ?? '',
        Active: selectedLookup?.Active ?? true,
        NumberOfChilds: selectedLookup?.NumberOfChilds ?? 0,
        DisplayOrder: selectedLookup?.DisplayOrder ?? 0,
      });
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        setShowSlider(false);
        removeParam('editLookup');
        setIsRefresh(!isRefresh);
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoader(false);
    }
  };

  /**
   * get all lookup types
   */
  const getAllLookupTypes = async () => {
    try {
      setLoader(true);
      const response = await listLookup({});
      setLookupTypes(response?.Lookups);
      setOriginalLookupTypes(response?.Lookups);
    } catch (error) {
      console.error('Error fetching lookup details:', error);
    } finally {
      setLoader(false);
    }
  };

  /**
   * Close the slider
   */
  const onCloseSlider = () => {
    removeParam('editLookup');
    setShowSlider(false);
  };

  React.useEffect(() => {
    getAllLookupTypes();
  }, []);

  return (
    <>
      <LookupContentSlider show={show}>
        <SliderCard
          handleSlide={onCloseSlider}
          title={`${editMode ? 'Edit' : 'Add'} Lookup`}
          className='test-doc-slider small-slider'
        >
          <SliderCard.Body>
            {loader && <Loader />}
            <Form
              onSubmit={onSave}
              render={(formRenderProps) => (
                <div className='card-content content-slider-form'>
                  <div className='single-field-row'>
                    <div
                      className={`field-wrapper multiselect-field-wrapper ${isValidate(validateForm, formData.Type) ? 'field-invalid' : 'field-valid'}`}
                    >
                      <FormAutoComplete
                        data={lookupTypes}
                        textField='Type'
                        id='Type'
                        label={`Type`}
                        value={formData?.Type}
                        onChange={onChange}
                        placeholder='Select Type'
                        disabled={editType ? false : true}
                        requiredField={true}
                      />
                      {isValidate(validateForm, formData?.Type) && (
                        <Error>This field cannot be empty</Error>
                      )}
                    </div>
                  </div>
                  <div className='single-field-row'>
                    <div
                      className={`field-wrapper ${isValidate(validateForm, formData.DisplayName) ? 'field-invalid' : 'field-valid'}`}
                    >
                      <Field
                        key={'DisplayName'}
                        id={'DisplayName'}
                        name={'DisplayName'}
                        data={formData?.DisplayName}
                        onChange={handleInputChange}
                        label={'Display Name'}
                        component={TextInput}
                        optional={false}
                        requiredField={true}
                      />
                      {isValidate(validateForm, formData?.DisplayName) && (
                        <Error>This field cannot be empty</Error>
                      )}
                    </div>
                  </div>
                  <div className='single-field-row'>
                    <div
                      className={`field-wrapper ${isValidate(validateForm, formData.Value) ? 'field-invalid' : 'field-valid'}`}
                    >
                      <Field
                        key={'Value'}
                        id={'Value'}
                        name={'Value'}
                        data={formData?.Value}
                        onChange={handleInputChange}
                        label={'Value'}
                        component={TextInput}
                        optional={false}
                        requiredField={true}
                      />
                      {isValidate(validateForm, formData?.Value) && (
                        <Error>This field cannot be empty</Error>
                      )}
                    </div>
                  </div>
                </div>
              )}
            />
          </SliderCard.Body>
          <SliderCard.Footer>
            <>
              <Button
                className={'small-slider-action'}
                icon='cancel'
                onClick={onCloseSlider}
                type='button'
              >
                Cancel
              </Button>
              <Button themeColor='primary' icon='save' onClick={handleSave}>
                Save
              </Button>
            </>
          </SliderCard.Footer>
        </SliderCard>
      </LookupContentSlider>
    </>
  );
};

export default AddLookupDetails;
